import React, { useMemo, useState } from 'react'
import { Device, Connection, SignalType } from '@/types/devices'
import { SIGNAL_TYPE_COLORS, SIGNAL_TYPE_LABELS } from '@/constants/colors'

interface RoutingMatrixProps {
  devices: Device[]
  connections: Connection[]
  onCellClick?: (sourceDeviceId: string, targetDeviceId: string) => void
  onDeleteConnection?: (connectionId: string) => void
}

export const RoutingMatrix: React.FC<RoutingMatrixProps> = ({
  devices,
  connections,
  onCellClick,
  onDeleteConnection,
}) => {
  const [hoveredCell, setHoveredCell] = useState<{
    source: string
    target: string
  } | null>(null)
  const [expandedCell, setExpandedCell] = useState<{
    source: string
    target: string
  } | null>(null)

  // Create a map of connections for quick lookup
  const connectionMap = useMemo(() => {
    const map: Record<string, Connection[]> = {}
    connections.forEach(conn => {
      const key = `${conn.sourceDeviceId}|${conn.targetDeviceId}`
      if (!map[key]) map[key] = []
      map[key].push(conn)
    })
    return map
  }, [connections])

  // Memoize grouped connections by source-target pair
  const getCellConnections = (sourceId: string, targetId: string) => {
    return connectionMap[`${sourceId}|${targetId}`] || []
  }

  // Render a single cell with connection indicators
  const renderCell = (sourceDevice: Device, targetDevice: Device) => {
    const cellConns = getCellConnections(sourceDevice.id, targetDevice.id)
    const isExpanded =
      expandedCell?.source === sourceDevice.id &&
      expandedCell?.target === targetDevice.id
    const isHovered =
      hoveredCell?.source === sourceDevice.id &&
      hoveredCell?.target === targetDevice.id

    if (cellConns.length === 0) {
      return (
        <div
          key={`${sourceDevice.id}-${targetDevice.id}`}
          className="border border-brd-subtle h-12 flex items-center justify-center cursor-pointer hover:bg-accent-muted transition-colors duration-150 relative group"
          onMouseEnter={() =>
            setHoveredCell({ source: sourceDevice.id, target: targetDevice.id })
          }
          onMouseLeave={() => setHoveredCell(null)}
          onClick={() => onCellClick?.(sourceDevice.id, targetDevice.id)}
        >
          <div className="opacity-0 group-hover:opacity-100 text-[10px] text-content-tertiary text-center px-1 transition-opacity duration-150">
            Click to connect
          </div>
        </div>
      )
    }

    return (
      <div
        key={`${sourceDevice.id}-${targetDevice.id}`}
        className={`border border-brd-subtle h-12 flex items-center justify-center relative transition-colors duration-150 ${
          isHovered ? 'bg-surface-overlay' : 'bg-surface-raised'
        } ${isExpanded ? 'ring-2 ring-accent' : ''}`}
        onMouseEnter={() =>
          setHoveredCell({ source: sourceDevice.id, target: targetDevice.id })
        }
        onMouseLeave={() => setHoveredCell(null)}
        onClick={() =>
          setExpandedCell(
            isExpanded
              ? null
              : { source: sourceDevice.id, target: targetDevice.id },
          )
        }
      >
        <div className="flex items-center gap-1 px-2">
          {cellConns.length === 1 ? (
            <div
              className="w-3 h-3 rounded-full"
              style={{
                backgroundColor:
                  SIGNAL_TYPE_COLORS[cellConns[0].signalType || 'audio'],
              }}
              title={`${cellConns[0].signalType ? SIGNAL_TYPE_LABELS[cellConns[0].signalType] : 'Unknown'}`}
            />
          ) : (
            <div className="flex items-center justify-center bg-surface-sunken rounded-full w-5 h-5 text-[10px] font-bold text-content-primary">
              {cellConns.length}
            </div>
          )}
        </div>

        {/* Expanded detail popup */}
        {isExpanded && (
          <div className="absolute bottom-full left-0 z-50 mb-2 bg-surface-raised border border-brd-emphasis rounded-xl shadow-high p-3 w-max min-w-[250px]">
            <div className="text-xs font-bold text-content-primary mb-2">
              {sourceDevice.name} → {targetDevice.name}
            </div>
            <div className="space-y-2">
              {cellConns.map((conn, idx) => {
                const sourcePort = sourceDevice.outputs.find(
                  p => p.id === conn.sourcePortId,
                )
                const targetPort = targetDevice.inputs.find(
                  p => p.id === conn.targetPortId,
                )
                return (
                  <div
                    key={conn.id}
                    className="flex items-center justify-between gap-3 text-[11px]"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{
                          backgroundColor:
                            SIGNAL_TYPE_COLORS[conn.signalType || 'audio'],
                        }}
                      />
                      <span className="text-content-secondary">
                        {sourcePort?.name || 'Unknown'} →{' '}
                        {targetPort?.name || 'Unknown'}
                      </span>
                    </div>
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        onDeleteConnection?.(conn.id)
                      }}
                      className="text-destructive hover:text-destructive/80 font-bold"
                      title="Delete connection"
                    >
                      ×
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  const rowHeight = 48
  const cellWidth = 48
  const labelWidth = 200

  return (
    <div className="h-full w-full flex flex-col bg-surface-base overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-brd-subtle">
        <h2 className="text-lg font-bold text-content-primary">
          Routing Matrix
        </h2>
        <p className="text-xs text-content-tertiary mt-1">
          Click cells to view connections, or click an empty cell to create a
          new connection
        </p>
      </div>

      {/* Matrix Container - Scrollable */}
      <div className="flex-1 overflow-auto">
        <div className="inline-block">
          {/* Column headers (device names, rotated 45deg) */}
          <div className="flex" style={{ marginLeft: `${labelWidth}px` }}>
            {devices.map(device => (
              <div
                key={`header-${device.id}`}
                className="flex items-end justify-center border-b border-brd-emphasis font-semibold text-content-primary p-2"
                style={{ width: `${cellWidth}px`, height: `${cellWidth}px` }}
              >
                <div
                  style={{
                    transform: 'rotate(-45deg)',
                    transformOrigin: 'center',
                    maxWidth: '100%',
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={device.name}
                >
                  {device.name}
                </div>
              </div>
            ))}
          </div>

          {/* Matrix rows */}
          {devices.map(sourceDevice => (
            <div key={`row-${sourceDevice.id}`} className="flex">
              {/* Row header (source device name) - sticky */}
              <div
                className="sticky left-0 z-10 bg-surface-sunken border-r border-brd-emphasis border-b border-brd-subtle flex items-center px-3 font-semibold text-xs text-content-primary"
                style={{ width: `${labelWidth}px`, height: `${rowHeight}px` }}
                title={sourceDevice.name}
              >
                <span className="truncate">{sourceDevice.name}</span>
              </div>

              {/* Matrix cells */}
              {devices.map(targetDevice => (
                <div
                  key={`cell-${sourceDevice.id}-${targetDevice.id}`}
                  style={{ width: `${cellWidth}px`, height: `${rowHeight}px` }}
                >
                  {renderCell(sourceDevice, targetDevice)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Summary footer */}
      <div className="bg-surface-sunken border-t border-brd-subtle px-4 py-2 text-sm text-content-secondary">
        <div className="flex justify-between items-center">
          <div>Total Connections: {connections.length}</div>
          <div className="text-xs">
            Matrix: {devices.length}×{devices.length} cells
          </div>
        </div>
      </div>
    </div>
  )
}
